/* Varianten-Inhalte auf der PDP synchron halten (BonaTea, Okt 2026).
   - product:rerender (vom Theme, auf dem Produkt-Formular): tauscht die Blöcke
     description + collapsible-text aus dem frisch gerenderten HTML-Fragment.
   - variant:change (vom Theme, auf dem Produkt-Formular): lädt Sections mit
     [data-variant-rerender] per Section Rendering API neu.
   Beide Events werden direkt am Formular abgehört. event.detail.formId taugt nicht zum
   Vergleich: Das Theme übergibt dort form.id, und das ist bei einem Formular das
   versteckte Feld name="id", nicht die ID des Formulars. */
class VariantContentSync extends HTMLElement {
  connectedCallback() {
    this._formId = this.getAttribute('form-id');
    this._productUrl = this.getAttribute('product-url') || window.location.pathname;
    this._blockTypes = (this.getAttribute('block-types') || 'description,collapsible-text').split(',').map((s) => s.trim());
    this._requestId = 0;
    this._abort = new AbortController();

    const form = document.forms[this._formId];
    if (!form) return;

    form.addEventListener('product:rerender', this._onRerender.bind(this), { signal: this._abort.signal });
    form.addEventListener('variant:change', this._onVariantChange.bind(this), { signal: this._abort.signal });
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  _onRerender(event) {
    const fragment = event.detail?.htmlFragment;
    if (!fragment || event.detail?.productChange) return;

    this._blockTypes.forEach((type) => {
      document.querySelectorAll(`[data-block-type="${type}"][data-block-id]`).forEach((current) => {
        const id = current.getAttribute('data-block-id');
        const fresh = fragment.querySelector(`[data-block-type="${type}"][data-block-id="${id}"]`);
        if (!fresh) return;

        // Auf/zu-Zustand eines Accordions beibehalten. Über die Eigenschaft open setzen, nicht nur
        // das Attribut: Chrome initialisiert das Theme-Accordion schon im Fragment, und nur der
        // Setter hält dessen internen Zustand synchron (sonst braucht der nächste Klick zwei Anläufe).
        const currentDetails = current.querySelector('details');
        const freshDetails = fresh.querySelector('details');
        if (currentDetails && freshDetails) {
          const open = currentDetails.hasAttribute('open');
          freshDetails.open = open;
          freshDetails.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        current.replaceWith(fresh);
      });
    });
  }

  async _onVariantChange(event) {
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;

    const sectionIds = Array.from(document.querySelectorAll('[data-variant-rerender]'))
      .map((el) => el.closest('.shopify-section')?.id?.replace('shopify-section-', ''))
      .filter(Boolean);
    if (sectionIds.length === 0) return;

    // Bei schnellen Wechseln zählt nur die letzte Anfrage – ältere Antworten werden verworfen
    const requestId = ++this._requestId;

    try {
      const response = await fetch(`${this._productUrl}?variant=${variantId}&sections=${sectionIds.join(',')}`);
      if (!response.ok) return;
      const sections = await response.json();
      if (requestId !== this._requestId) return;

      sectionIds.forEach((id) => {
        const html = sections[id];
        const current = document.getElementById(`shopify-section-${id}`);
        if (!html || !current) return;

        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const fresh = template.content.firstElementChild;
        if (!fresh) return;

        // Scroll-Animationen des Themes greifen bei nachgeladenen Knoten nicht – Attribut entfernen, damit nichts unsichtbar bleibt
        fresh.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        current.replaceWith(fresh);
      });
    } catch (e) {
      // still bleiben – die Seite zeigt dann weiterhin den Stand vor dem Wechsel
    }
  }
}

if (!window.customElements.get('variant-content-sync')) {
  window.customElements.define('variant-content-sync', VariantContentSync);
}
